import { useNavigate, useParams } from 'react-router-dom';

import type {
    AttemptQuestionDetailsDTO,
    NavigationQuestionDTO,
} from '../models/StudentExamViewDTOs';
import type { ActiveTool } from './studentTools/HighlightableWrapper';
import { EraserIcon, HighlighterIcon, NotepadIcon, SimpleSwitch } from '@/src/ui';
import { translate } from '@/src/utils';
import '../ViewQuestionPage.css';
import './studentTools/StudentTools.css';

const replaceNav = { replace: true };

type QuestionToolsProps = {
    loading: boolean;
    setError: (err: string) => void;
    language: 'en' | 'es';
    setLanguage: (lang: 'en' | 'es') => void;
    showToggle: boolean;
    questionDetails: AttemptQuestionDetailsDTO;
    saveQuestion: (callback?: () => void) => void;
    toggleQuestionFlag: (isFlaggedForReview: boolean) => void;
    navData?: NavigationQuestionDTO[];
    isSegment: boolean;
    activeTool: ActiveTool;
    onSetActiveTool: (tool: ActiveTool) => void;
    onOpenNotepad: () => void;
};

export default function QuestionTools({
    loading,
    setError,
    language,
    setLanguage,
    showToggle,
    questionDetails,
    saveQuestion,
    toggleQuestionFlag,
    navData,
    isSegment,
    activeTool,
    onSetActiveTool,
    onOpenNotepad,
}: QuestionToolsProps) {
    const isFlaggedForReview = questionDetails.isFlaggedForReview;

    const navigate = useNavigate();
    const { attemptId = '0', attemptQuestionId = '0' } = useParams();

    const handleFlagQuestion = () => {
        if (isSegment) return;
        toggleQuestionFlag(!isFlaggedForReview);
    };

    const currentIndex = navData?.findIndex((q) => q.id === questionDetails.id) || 0;
    const isLastQuestion = currentIndex === (navData?.length || 0) - 1;
    const isBackDisabled = (() => {
        if (!navData) {
            return false;
        }

        const prevQuestionSegmentNumber = navData[currentIndex - 1]?.segmentNumber;
        const currentQuestionSegmentNumber = navData[currentIndex].segmentNumber;

        return currentIndex <= 0 || prevQuestionSegmentNumber !== currentQuestionSegmentNumber;
    })();

    const isNextDisabled = (() => {
        if (!navData || isLastQuestion) {
            return false;
        }

        const currentQuestionSegmentNumber = navData[currentIndex].segmentNumber;
        const nextQuestionSegmentNumber = navData[currentIndex + 1]?.segmentNumber;

        return (
            currentIndex >= navData.length - 1 ||
            nextQuestionSegmentNumber !== currentQuestionSegmentNumber
        );
    })();

    const handleBack = async () => {
        try {
            if (!navData) {
                return;
            }
            setError('');

            if (isBackDisabled) {
                return;
            } else {
                const prevQuestionId = navData[currentIndex - 1].id;
                saveQuestion(() =>
                    navigate(
                        `/studentExamView/attempt/${attemptId}/q/${prevQuestionId}`,
                        replaceNav
                    )
                );
            }
        } catch {
            setError('Failed to navigate to previous question.');
        }
    };

    const handleNext = async () => {
        try {
            if (!navData) {
                return;
            }
            setError('');

            if (isNextDisabled && !isLastQuestion) {
                return;
            } else {
                if (!isLastQuestion) {
                    const nextQuestionId = navData[currentIndex + 1].id;
                    saveQuestion(() =>
                        navigate(
                            `/studentExamView/attempt/${attemptId}/q/${nextQuestionId}`,
                            replaceNav
                        )
                    );
                } else {
                    saveQuestion(() =>
                        navigate(
                            `/studentExamView/attempt/${attemptId}/summary/${attemptQuestionId}`
                        )
                    );
                }
            }
        } catch {
            setError('Failed to navigate to next question');
        }
    };

    const handleToggleTool = (tool: 'highlight' | 'erase') => {
        onSetActiveTool(activeTool === tool ? 'none' : tool);
    };

    return (
        <div className="assessment-question-tools flex flex-row py-4 gap-x-4">
            <div className="flex flex-row gap-x-4">
                <button
                    onClick={handleBack}
                    className="assessment-nav-btn blue"
                    disabled={loading || isBackDisabled}
                >
                    {translate(language, 'back')}
                </button>

                <button
                    onClick={handleNext}
                    className="assessment-nav-btn blue"
                    disabled={loading || isNextDisabled}
                >
                    {translate(language, isLastQuestion ? 'review' : 'next')}
                </button>
            </div>

            <div className="flex flex-row gap-x-4 ml-auto">
                {showToggle && (
                    <SimpleSwitch
                        name="toggleSpanish"
                        checked={language === 'es'}
                        onChange={(e) => setLanguage(e.target.checked ? 'es' : 'en')}
                        sx={switchSx}
                        label={translate(language, 'selected_language')}
                    />
                )}

                <button
                    onClick={() => handleToggleTool('highlight')}
                    className={[
                        'assessment-nav-btn',
                        'btn-tool',
                        activeTool === 'highlight' && 'tool-active-highlight',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    disabled={loading || isSegment}
                    title={translate(language, 'highlight_tool')}
                >
                    <HighlighterIcon />
                </button>

                <button
                    onClick={() => handleToggleTool('erase')}
                    className={[
                        'assessment-nav-btn',
                        'btn-tool',
                        activeTool === 'erase' && 'tool-active-erase',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    disabled={loading || isSegment}
                    title={translate(language, 'erase_tool')}
                >
                    <EraserIcon />
                </button>

                <button
                    onClick={onOpenNotepad}
                    className="assessment-nav-btn btn-tool"
                    disabled={loading || isSegment}
                    title={translate(language, 'notepad')}
                >
                    <NotepadIcon />
                </button>

                <button
                    onClick={handleFlagQuestion}
                    className={[
                        'assessment-nav-btn red',
                        'btn-flag',
                        isFlaggedForReview && 'active',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    disabled={loading || isSegment}
                    title={
                        isFlaggedForReview
                            ? translate(language, 'flag_off')
                            : translate(language, 'flag_for_review')
                    }
                >
                    🚩
                </button>
            </div>
        </div>
    );
}

const switchSx = {
    '& .MuiSwitch-switchBase': {
        color: '#20a2dc',
        '&.Mui-checked': {
            color: '#20a2dc',
            '& + .MuiSwitch-track': {
                backgroundColor: '#1a82b3',
                opacity: 1,
            },
        },
    },
    '& .MuiSwitch-track': {
        backgroundColor: '#cfeefd',
        opacity: 1,
    },
};
